import React, {useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {Control, Controller, useFieldArray, useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import 'core/yupCustomValidations';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Select} from 'components/ui-kit/Select';
import {ProductGroups} from 'core/constants/productGroups';
import {createOrder, products$, productsChanged} from 'models/orders/create';
import {balance$} from 'models/profile/billing';
import {colors} from 'theme';

import {OrderProductsData} from 'types';
import {PackageTypes} from 'types/gtins';

import {PackageTypesOptions} from 'utils/packageTypes-mappers';
import {mapArrayToI18nOptions, mapEnumToI18nOptions} from 'utils/select';

import {
    AvailableCodesContainer,
    CountErrorMessage,
    Fieldset,
    FormContainer,
    FormField,
    FormPartTitle,
    NavigationButtonsContainer,
} from '../styled';

const productsSchema = yup
    .array()
    .of(
        yup.object({
            productGroupCode: yup
                .string()
                .oneOf(Object.keys(ProductGroups))
                .required(
                    `${i18next.t('components.form.required')}: ${i18next.t(
                        'pages.orders.form.quantity',
                    )}`,
                ),
            quantity: yup
                .number()
                .typeError(
                    `${i18next.t('components.form.wrong')} ${i18next.t(
                        'pages.orders.form.quantity',
                    )}`,
                )
                .positive(
                    `${i18next.t('components.form.wrong')} ${i18next.t(
                        'pages.orders.form.quantity',
                    )}: ${'${value}'}`,
                )
                .integer(
                    `${i18next.t('components.form.wrong')} ${i18next.t(
                        'pages.orders.form.quantity',
                    )}: ${'${value}'}`,
                )
                .max(
                    10000,
                    `${i18next.t('components.form.wrong')} ${i18next.t(
                        'pages.orders.form.quantity',
                    )}: ${'${value}'}`,
                )
                .required(
                    `${i18next.t('components.form.required')}: ${i18next.t(
                        'pages.orders.form.quantity',
                    )}`,
                ),
            type: yup
                .string()
                .oneOf(
                    // ['UNIT', 'GROUP'],
                    Object.keys(PackageTypes),
                    `${i18next.t('components.form.wrong')} ${i18next.t(
                        'pages.orders.form.type',
                    )}: ${'${value}'}`,
                )
                .required(
                    `${i18next.t('components.form.required')}: ${i18next.t(
                        'pages.orders.form.type',
                    )}`,
                ),
        }),
    )
    .min(1, `${i18next.t('components.form.min')} 1`)
    .max(10, `${i18next.t('components.form.max')} 10`);

const schema = yup.object().shape({
    products: productsSchema,
});

interface FormItemProps {
    control: Control;
    defaultValue: string;
    name: string;
    setValue?: Function;
    fieldName?: string;
    getValues: Function;
}

export const ProductGroupCode = ({control, setValue, fieldName, ...props}: FormItemProps) => {
    const {t} = useTranslation();
    const options = mapEnumToI18nOptions(ProductGroups, 'dictionaries.productGroups');

    return (
        <>
            <Controller
                control={control}
                {...props}
                render={({value, onChange}, {invalid}) => (
                    <div style={{position: 'relative'}}>
                        <Select
                            label={t('pages.orders.form.productGroup')}
                            isClearable
                            defaultOptions
                            value={options.find((v) => v.value === value)}
                            onChange={(v: {value: string}) => {
                                onChange(v?.value ?? null);
                                setValue?.(`${fieldName}.productGroupCode`, v?.value);
                            }}
                            options={options}
                            hasError={invalid}
                        />
                    </div>
                )}
            />
        </>
    );
};

export const ProductType = ({control, setValue, fieldName, ...props}: FormItemProps) => {
    const {t} = useTranslation();
    const options = mapArrayToI18nOptions(
        PackageTypesOptions.filter((o) => !['UNIT', 'GROUP', 'CUSTOMS_UNIT'].includes(o.value)),
        'dictionaries.products.packageType',
    );
    return (
        <>
            <Controller
                control={control}
                {...props}
                render={({value, onChange}, {invalid}) => (
                    <div style={{position: 'relative'}}>
                        <Select
                            label={t('pages.orders.form.type')}
                            isClearable
                            defaultOptions
                            value={options.find((v) => v.value === value)}
                            onChange={(v: {value: string}) => {
                                onChange(v?.value ?? null);
                                setValue?.(`${fieldName}.type`, v?.value);
                            }}
                            options={options}
                            hasError={invalid}
                        />
                    </div>
                )}
            />
        </>
    );
};

export const OrderProducts = () => {
    const {t} = useTranslation();
    const products = useStore(products$);
    const balance = useStore(balance$);
    const {control, handleSubmit, formState, setValue, getValues} = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            products:
                products.length > 0 ? products : [{quantity: '', type: '', productGroupCode: ''}],
        },
    });
    const {fields, remove} = useFieldArray({
        control,
        name: 'products',
    });
    const watchedProducts = useWatch({
        control,
        name: 'products',
    });
    const totalQuantity = useMemo(
        () =>
            (watchedProducts as OrderProductsData[])?.reduce(
                (sum, item) => sum + (Number(item.quantity) || 0),
                0,
            ),
        [watchedProducts],
    );

    const codesLeft = useMemo(
        () => (balance - Math.abs(totalQuantity) < 0 ? 0 : balance - Math.abs(totalQuantity)),
        [totalQuantity, balance],
    );

    const onHandleChanged = ({products}: {products: Array<OrderProductsData>}) => {
        if (!products.length) return;
        productsChanged(products);
        createOrder();
    };
    return (
        <FormContainer>
            {/*<Grid container spacing={3}>*/}
            {/*    <Grid item md={4} xs={12}>*/}
            {/*        <FormPartTitle style={{marginTop: 10}}>*/}
            {/*            {t('pages.orders.form.productListTitle')}*/}
            {/*        </FormPartTitle>*/}
            {/*    </Grid>*/}
            {/*    <Grid item md={8} xs={12}>*/}
            {/*        <XlsxFileForm*/}
            {/*            onChange={handleFileChanged}*/}
            {/*            scheme={productsSchema}*/}
            {/*            assessors={{*/}
            {/*                gtin: 'A',*/}
            {/*                quantity: 'B',*/}
            {/*                type: 'C',*/}
            {/*            }}*/}
            {/*            initialValue={file}*/}
            {/*            linkExample="/examples/order_example.xlsx"*/}
            {/*            linkExampleAction={(path) => {*/}
            {/*                downloadXlsx({*/}
            {/*                    path,*/}
            {/*                    localePath: 'examples.order',*/}
            {/*                });*/}
            {/*            }}*/}
            {/*        />*/}
            {/*    </Grid>*/}
            {/*</Grid>*/}
            <Grid container spacing={4}>
                <Grid item md={3}>
                    <FormPartTitle style={{marginTop: 10}}>
                        {t('pages.orders.form.transportOrCustomListTitle')}
                    </FormPartTitle>
                </Grid>
                <Grid item md={9}>
                    <form onSubmit={handleSubmit(onHandleChanged)}>
                        {fields.map((field, index) => {
                            const fieldName = `products[${index}]`;
                            return (
                                <Fieldset name={fieldName} key={field.id}>
                                    <FormField>
                                        <Grid container spacing={3}>
                                            {/*<Grid item md={4} xs={12}>*/}
                                            {/*    <ProductGroupCode*/}
                                            {/*        control={control}*/}
                                            {/*        defaultValue={field.productGroupCode}*/}
                                            {/*        name={`${fieldName}.productGroupCode`}*/}
                                            {/*        fieldName={fieldName}*/}
                                            {/*        setValue={setValue}*/}
                                            {/*        getValues={getValues}*/}
                                            {/*    />*/}
                                            {/*</Grid>*/}
                                            <Grid item md={4} xs={12}>
                                                <ProductType
                                                    control={control}
                                                    defaultValue={field.type}
                                                    name={`${fieldName}.type`}
                                                    fieldName={fieldName}
                                                    setValue={setValue}
                                                    getValues={getValues}
                                                />
                                            </Grid>
                                            <Grid item md={4} xs={12}>
                                                <Controller
                                                    control={control}
                                                    name={`${fieldName}.quantity`}
                                                    defaultValue={field.quantity}
                                                    render={(props, {invalid}) => (
                                                        <Input
                                                            {...props}
                                                            label={t('pages.orders.form.quantity')}
                                                            hasError={invalid}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <input
                                                type="submit"
                                                disabled={
                                                    totalQuantity > balance ||
                                                    (formState.isValid === false &&
                                                        !products.length)
                                                }
                                                hidden
                                            />
                                            <div style={{display: 'none'}}>
                                                <Controller
                                                    control={control}
                                                    name={`${fieldName}.productGroupCode`}
                                                    defaultValue={'TOBACCO'}
                                                    render={() => (
                                                        <input readOnly value="TOBACCO" />
                                                    )}
                                                />
                                            </div>
                                            <Grid item md={2} xs={12}>
                                                {index !== 0 && (
                                                    <CrossIcon
                                                        style={{
                                                            width: 12,
                                                            height: 12,
                                                            marginLeft: 10,
                                                            marginBottom: -3,
                                                            cursor: 'pointer',
                                                            marginTop: 37,
                                                            fill: colors.danger,
                                                        }}
                                                        onClick={() => remove(index)}
                                                    />
                                                )}
                                            </Grid>
                                        </Grid>
                                    </FormField>
                                </Fieldset>
                            );
                        })}
                        {/*<Button*/}
                        {/*    type="button"*/}
                        {/*    style={{width: 200}}*/}
                        {/*    variant={ButtonVariant.Normal}*/}
                        {/*    disabled={fields.length >= 10}*/}
                        {/*    onClick={() => {*/}
                        {/*        append({productGroupCode: '', quantity: '', type: ''});*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    + {t('pages.orders.form.add')}*/}
                        {/*</Button>*/}
                        <AvailableCodesContainer
                            noAvailable={balance === 0 || balance < Math.abs(totalQuantity)}
                        >
                            {t('components.form.codesAvailableLeft')}: {codesLeft}
                        </AvailableCodesContainer>
                        {fields.length >= 10 && (
                            <CountErrorMessage>{`${i18next.t(
                                'components.form.max',
                            )} 10`}</CountErrorMessage>
                        )}
                    </form>
                </Grid>
            </Grid>
            <NavigationButtonsContainer>
                <Button
                    style={{width: 'auto', marginLeft: 'auto'}}
                    variant={ButtonVariant.Normal}
                    disabled={
                        totalQuantity > balance || (formState.isValid === false && !products.length)
                    }
                    onClick={handleSubmit(onHandleChanged)}
                >
                    {t('pages.orders.form.submit')}
                </Button>
                {/*<Button*/}
                {/*    style={{width: 'auto'}}*/}
                {/*    variant={ButtonVariant.Normal}*/}
                {/*    onClick={() => stepChanged(0)}*/}
                {/*>*/}
                {/*    {t('components.form.prevStep')}*/}
                {/*</Button>*/}
                {/*<Button*/}
                {/*    style={{width: 'auto'}}*/}
                {/*    variant={ButtonVariant.Normal}*/}
                {/*    type="submit"*/}
                {/*    disabled={*/}
                {/*        totalQuantity >= balance ||*/}
                {/*        (formState.isValid === false && !products.length) ||*/}
                {/*        (!!file && !products.length)*/}
                {/*    }*/}
                {/*    onClick={handleSubmit(onHandleChanged)}*/}
                {/*>*/}
                {/*    {t('components.form.nextStep')}*/}
                {/*</Button>*/}
            </NavigationButtonsContainer>
        </FormContainer>
    );
};
